import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Stack,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEmployeeAuth } from '../../hooks/useEmployeeAuth';
import { useEmployeeData } from '../../hooks/useEmployeeData';
import { useRoles } from '../../hooks/useFirestoreData';
import { getWeekStart } from '../../utils';
import { getBusinessSettings } from '../../utils/businessDay';
import EmployeeMobileLayout from './EmployeeMobileLayout';

// Helper function to format time (improved with duration calculation)
const formatTime = (timeString) => {
  if (!timeString) return 'Time TBD';
  const [hours, minutes] = timeString.split(':');
  const hour24 = parseInt(hours, 10);
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  return `${hour12}:${minutes} ${ampm}`;
};

// Helper function to calculate shift duration
const getShiftDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return '';
  
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);
  
  let startTotalMinutes = startHours * 60 + startMinutes;
  let endTotalMinutes = endHours * 60 + endMinutes;
  
  // Handle overnight shifts
  if (endTotalMinutes < startTotalMinutes) {
    endTotalMinutes += 24 * 60;
  }
  
  const durationMinutes = endTotalMinutes - startTotalMinutes;
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  
  if (hours === 0) return `${minutes}min`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}min`;
};

// Helper function to get week dates starting Monday
const getWeekDates = (startDate) => {
  const dates = [];
  const current = new Date(startDate);
  
  for (let i = 0; i < 7; i++) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

export default function EmployeeDashboardSimple() {
  const { employee } = useEmployeeAuth();
  const { shifts, locations, loading } = useEmployeeData(employee?.id);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekDates, setWeekDates] = useState([]);
  const [filteredShifts, setFilteredShifts] = useState([]);
  
  // Load roles from Firestore
  const roles = useRoles();

  // Helper function to get role name from role ID or code
  const getRoleName = (roleId) => {
    if (!roleId) return 'Staff';
    
    // First, try to find the role in the Firestore roles collection
    const role = roles.find(r => r.id === roleId);
    if (role && role.name) {
      return role.name;
    }
    
    // Common role mappings (fallback)
    const roleMap = {
      'sales_associate': 'Sales Associate',
      'cashier': 'Cashier',
      'manager': 'Manager',
      'assistant_manager': 'Assistant Manager',
      'cook': 'Cook',
      'server': 'Server',
      'host': 'Host',
      'bartender': 'Bartender',
      'supervisor': 'Supervisor',
      'team_lead': 'Team Lead',
      'shift_lead': 'Shift Lead',
      'barista': 'Barista',
      'kitchen_staff': 'Kitchen Staff'
    };
    
    // If it's a mapped role code, return the friendly name
    if (roleMap[roleId]) {
      return roleMap[roleId];
    }
    
    // If it looks like a Firestore ID (long random string), return generic role
    if (roleId.length > 15 && /^[a-zA-Z0-9]+$/.test(roleId)) {
      return 'Staff';
    }
    
    // Otherwise, try to format it nicely
    return roleId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Initialize week dates starting at company week start (falls back to Monday)
  useEffect(() => {
    (async () => {
      const today = new Date();
      let startDay = 'Monday';
      try {
        const settings = await getBusinessSettings();
        startDay = (settings && settings.company && settings.company.startWeekOn) || settings.startWeekOn || startDay;
      } catch (e) {
        // ignore and use default
      }
      const startOfWeek = getWeekStart(today, startDay);
      setWeekDates(getWeekDates(startOfWeek));
      setSelectedDate(today);
    })();
  }, []);

  // Filter shifts for selected date and following 3 weeks (21 days)
  useEffect(() => {
    if (shifts && selectedDate) {
      const endDate = new Date(selectedDate);
      endDate.setDate(selectedDate.getDate() + 21); // 3 weeks from selected date
      
      const { toDate } = require('../../utils/shiftDate');
      const filtered = shifts.filter(shift => {
        const shiftDate = shift.shiftDateKey ? toDate(shift.shiftDateKey) : toDate(shift.shiftDate);
        const shiftDateOnly = new Date(shiftDate.getFullYear(), shiftDate.getMonth(), shiftDate.getDate());
        const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
        const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

        return shiftDateOnly >= selectedDateOnly && shiftDateOnly < endDateOnly;
      }).sort((a, b) => {
        const dateA = a.shiftDateKey ? toDate(a.shiftDateKey) : toDate(a.shiftDate);
        const dateB = b.shiftDateKey ? toDate(b.shiftDateKey) : toDate(b.shiftDate);
        return dateA - dateB;
      });
      setFilteredShifts(filtered);
    }
  }, [shifts, selectedDate]);

  // Get location name helper
  const getLocationName = (locationId) => {
    if (!locationId || !locations) return 'Location TBD';
    const location = locations.find(loc => loc.id === locationId);
    return location?.name || 'Location TBD';
  };

  // Navigate week
  const navigateWeek = (direction) => {
    const newStartDate = new Date(weekDates[0]);
    newStartDate.setDate(newStartDate.getDate() + (direction * 7));
    setWeekDates(getWeekDates(newStartDate));
  };

  if (loading) {
    return (
      <EmployeeMobileLayout title="My Schedule" employee={employee}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          gap: 2
        }}>
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">
            Loading your schedule...
          </Typography>
        </Box>
      </EmployeeMobileLayout>
    );
  }

  return (
    <EmployeeMobileLayout title="My Schedule" employee={employee}>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        {/* Week Navigation */}
        <Box sx={{ 
          p: 2, 
          bgcolor: 'primary.main', 
          color: 'primary.contrastText',
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Button 
              onClick={() => navigateWeek(-1)}
              sx={{ 
                color: 'primary.contrastText', 
                minWidth: 40, 
                height: 40,
                borderRadius: '50%',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              ←
            </Button>
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              {weekDates[0]?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </Typography>
            <Button 
              onClick={() => navigateWeek(1)}
              sx={{ 
                color: 'primary.contrastText', 
                minWidth: 40, 
                height: 40,
                borderRadius: '50%',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              →
            </Button>
          </Box>

          {/* Date Selector Row */}
          <Box sx={{ 
            display: 'flex', 
            overflowX: 'auto', 
            gap: 1,
            pb: 1,
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none'
          }}>
            {weekDates.map((date, index) => {
              const isSelected = date.toDateString() === selectedDate.toDateString();
              const isToday = date.toDateString() === new Date().toDateString();
              
              return (
                <Box
                  key={index}
                  onClick={() => setSelectedDate(date)}
                  sx={{
                    minWidth: 48,
                    height: 64,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    borderRadius: 2,
                    cursor: 'pointer',
                    bgcolor: isSelected ? 'rgba(255,255,255,0.25)' : 'transparent',
                    border: isToday ? '2px solid rgba(255,255,255,0.8)' : '2px solid transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.15)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                    {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'][index]}
                  </Typography>
                  <Typography variant="h6" sx={{ 
                    fontWeight: isSelected ? 'bold' : 'medium',
                    fontSize: '1.1rem'
                  }}>
                    {date.getDate()}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Selected Date Display */}
        <Box sx={{ 
          p: 2, 
          bgcolor: 'background.paper', 
          borderBottom: 1, 
          borderColor: 'divider',
          boxShadow: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 'medium' }}>
            {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'long', 
              day: 'numeric' 
            })} - Next 3 Weeks
          </Typography>
          <Tooltip title="Refresh schedule">
            <IconButton 
              onClick={() => window.location.reload()} 
              size="small"
              sx={{ 
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Schedule Content */}
        <Box sx={{ p: 2, bgcolor: 'background.default' }}>
          <Stack spacing={3}>
            {/* Generate all days for 3 weeks starting from selected date */}
            {Array.from({ length: 21 }, (_, index) => {
              const currentDate = new Date(selectedDate);
              currentDate.setDate(selectedDate.getDate() + index);
              const dateKey = currentDate.toDateString();
              const dayShifts = filteredShifts.filter(shift => {
                const shiftDate = shift.shiftDate?.toDate ? shift.shiftDate.toDate() : new Date(shift.shiftDate);
                return shiftDate.toDateString() === dateKey;
              });
              const isToday = currentDate.toDateString() === new Date().toDateString();
              
              return (
                <Box key={dateKey}>
                  {/* Date Header */}
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      mb: 2, 
                      color: isToday ? 'primary.main' : 'text.primary',
                      fontWeight: 'bold',
                      borderBottom: 2,
                      borderColor: isToday ? 'primary.main' : 'divider',
                      pb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    {currentDate.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                    {isToday && (
                      <Chip 
                        label="Today" 
                        color="primary" 
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    )}
                  </Typography>

                  {/* Shifts for this date or no shift message */}
                  {dayShifts.length === 0 ? (
                    <Card sx={{ 
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      opacity: 0.7
                    }}>
                      <CardContent sx={{ py: 2, px: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No scheduled shift
                        </Typography>
                      </CardContent>
                    </Card>
                  ) : (
                    <Stack spacing={2}>
                      {dayShifts.map((shift, shiftIndex) => {
                        const isUpcoming = currentDate >= new Date();
                        
                        return (
                          <Card key={shiftIndex} sx={{ 
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 2,
                            overflow: 'hidden',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: 'primary.main',
                              transform: 'translateY(-1px)',
                              boxShadow: 2
                            }
                          }}>
                            <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Box sx={{ 
                                  minWidth: 90,
                                  textAlign: 'center',
                                  p: 1.5,
                                  pb: 3, // More padding at bottom for duration
                                  bgcolor: 'primary.main',
                                  color: 'primary.contrastText',
                                  borderRadius: 2,
                                  position: 'relative'
                                }}>
                                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                                    {formatTime(shift.shiftStartTime)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', my: 0.25 }}>
                                    to
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                                    {formatTime(shift.shiftEndTime)}
                                  </Typography>
                                  {shift.shiftStartTime && shift.shiftEndTime && (
                                    <Typography variant="caption" sx={{ 
                                      position: 'absolute',
                                      bottom: 6,
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      bgcolor: 'rgba(255,255,255,0.2)',
                                      color: 'primary.contrastText',
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: 1,
                                      fontSize: '0.7rem',
                                      fontWeight: 'bold'
                                    }}>
                                      {getShiftDuration(shift.shiftStartTime, shift.shiftEndTime)}
                                    </Typography>
                                  )}
                                </Box>
                                
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="body1" fontWeight="bold" color="text.primary" sx={{ mb: 0.5 }}>
                                    {getRoleName(shift.role)}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    📍 {getLocationName(shift.locationId)}
                                  </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                  {isUpcoming && (
                                    <Chip 
                                      label="Upcoming" 
                                      color="success"
                                      variant="outlined"
                                      size="small" 
                                      sx={{ 
                                        fontWeight: 'medium',
                                        fontSize: '0.7rem'
                                      }} 
                                    />
                                  )}
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>
    </EmployeeMobileLayout>
  );
}
